import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from "framer-motion";

const CustomCalendar = ({ selectedDate, onChange }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date(selectedDate || Date.now()));
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const calendarRef = useRef(null);

  const months = [
    'January', 'February', 'March', 'April',
    'May', 'June', 'July', 'August',
    'September', 'October', 'November', 'December'
  ];

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const handleDateClick = (day) => {
    const newDate = new Date(currentMonth);
    newDate.setDate(day);
    onChange(newDate.toISOString().split('T')[0]);
  };

  const changeMonth = (increment) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + increment);
    setCurrentMonth(newMonth);
  };

  const selectMonth = (monthIndex) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(monthIndex);
    setCurrentMonth(newMonth);
    setShowMonthPicker(false);
  };

  const selectYear = (year) => {
    const newDate = new Date(currentMonth);
    newDate.setFullYear(year);
    setCurrentMonth(newDate);
    setShowYearPicker(false);
  };

  const renderDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const daysArray = [];

    for (let i = 0; i < firstDay; i++) {
      daysArray.push(<div key={`empty-${i}`} className="w-8 h-8"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentMonth.getFullYear()}-${(currentMonth.getMonth() + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      const isSelected = selectedDate === dateStr;

      daysArray.push(
        <button
          key={`day-${day}`}
          onClick={() => handleDateClick(day)}
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors
          ${isSelected ? 'bg-indigo-600 text-white' : 'hover:bg-indigo-100 text-gray-700'}`}
        >
          {day}
        </button>
      );
    }

    return daysArray;
  };

  useEffect(() => {
    if (selectedDate) {
      const selected = new Date(selectedDate);
      const sameMonth = selected.getMonth() === currentMonth.getMonth() &&
        selected.getFullYear() === currentMonth.getFullYear();
      if (!sameMonth) {
        setCurrentMonth(selected);
      }
    }
  }, [selectedDate]);

  return (
    <div ref={calendarRef} className="bg-white rounded-xl relative p-4 shadow-md ">
      <div className="flex justify-between items-center mb-4 gap-4">
        <button
          onClick={() => changeMonth(-1)}
          className="p-1 rounded-full hover:bg-gray-100 text-gray-600"
        >
          <i className="ri-arrow-left-s-line text-lg"></i>
        </button>

        <div className="flex gap-2">
          <button
            onClick={() => {
              setShowMonthPicker(!showMonthPicker);
              setShowYearPicker(false);
            }}
            className="font-QSemiBold text-gray-800 px-2 py-1 rounded hover:bg-gray-100 border border-gray-200"
          >
            {months[currentMonth.getMonth()]}
          </button>

          <button
            onClick={() => {
              setShowYearPicker(!showYearPicker);
              setShowMonthPicker(false);
            }}
            className="font-QSemiBold text-gray-800 px-2 py-1 rounded hover:bg-gray-100 border border-gray-200"
          >
            {currentMonth.getFullYear()}
          </button>
        </div>

        <button
          onClick={() => changeMonth(1)}
          className="p-1 rounded-full hover:bg-gray-100 text-gray-600"
        >
          <i className="ri-arrow-right-s-line text-lg"></i>
        </button>
      </div>

      <AnimatePresence>
        {showMonthPicker && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-20 bg-white rounded-lg shadow-lg grid grid-cols-3 gap-2 mt-1 border border-gray-200 p-2"
          >
            {months.map((month, index) => (
              <button
                key={month}
                onClick={() => selectMonth(index)}
                className={`px-3 py-1 font-QSemiBold rounded text-sm ${currentMonth.getMonth() === index
                  ? 'bg-indigo-600 text-white'
                  : 'hover:bg-indigo-50 text-gray-700'
                  }`}
              >
                {month.substring(0, 3)}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showYearPicker && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-20 bg-white rounded-lg shadow-lg grid grid-cols-3 gap-2 mt-1 border border-gray-200 p-2 max-h-60 overflow-y-auto"
          >
            {Array.from({ length: 20 }, (_, i) => {
              const year = new Date().getFullYear() - 10 + i;
              return (
                <button
                  key={year}
                  onClick={() => selectYear(year)}
                  className={`px-3 py-1 font-QSemiBold rounded text-sm ${currentMonth.getFullYear() === year
                    ? 'bg-indigo-600 text-white'
                    : 'hover:bg-indigo-50 text-gray-700'
                    }`}
                >
                  {year}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {days.map(day => (
          <div key={day} className="text-center text-xs font-QMedium text-gray-500">
            {day}
          </div>
        ))}
      </div>

      <div className="grid font-QMedium grid-cols-7 gap-1">
        {renderDays()}
      </div>
    </div>
  );
};

export default CustomCalendar;
